import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { CalendarCheck, Clock, MapPin, Search, User, Stethoscope, X, Edit2, Loader2, Check, AlertTriangle } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { API } from "@/config/api";
import { AREAS, REP_DIRECTORY, getRepDisplayName, APPOINTMENT_TYPES } from "@/config/constants";

const toISODate = (d) => d.toISOString().split("T")[0];
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const diffDays = (from, to) => Math.max(1, Math.ceil((to - from) / (1000 * 60 * 60 * 24)));

const today = () => new Date(new Date().toDateString());

const QUICK_RANGES = [
  { key: "today", label: "Today", from: () => today(), to: () => today() },
  { key: "week", label: "This Week", from: () => today(), to: () => {
    const d = today(); d.setDate(d.getDate() + (7 - d.getDay())); return d;
  }},
  { key: "14", label: "14 Days", from: () => today(), to: () => addDays(today(), 14) },
  { key: "30", label: "30 Days", from: () => today(), to: () => addDays(today(), 30) },
  { key: "60", label: "60 Days", from: () => today(), to: () => addDays(today(), 60) },
];

const formatDate = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

const isToday = (dateStr) => toISODate(new Date()) === dateStr;

const getDefaults = () => ({
  fromDate: toISODate(today()),
  toDate: toISODate(addDays(today(), 30)),
  activeRange: "30",
  repFilter: "",
  regionFilter: "",
  search: "",
});

const UpcomingPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const defaults = getDefaults();
  const [fromDate, setFromDate] = useState(defaults.fromDate);
  const [toDate, setToDate] = useState(defaults.toDate);
  const [activeRange, setActiveRange] = useState(defaults.activeRange);
  const [repFilter, setRepFilter] = useState(defaults.repFilter);
  const [regionFilter, setRegionFilter] = useState(defaults.regionFilter);
  const [search, setSearch] = useState(defaults.search);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => setUser(res.data))
      .catch(() => {});
  }, []);

  const apiDays = useMemo(() => {
    const to = new Date(toDate + "T00:00:00");
    return Math.max(1, diffDays(today(), to));
  }, [toDate]);

  useEffect(() => {
    const fetchUpcoming = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ days: String(apiDays) });
        if (repFilter) params.append("rep_name", repFilter);
        const res = await axios.get(`${API}/upcoming?${params}`, { withCredentials: true });
        setAppointments(res.data);
      } catch {
        setAppointments([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUpcoming();
  }, [apiDays, repFilter]);

  const applyQuickRange = useCallback((key) => {
    const range = QUICK_RANGES.find(r => r.key === key);
    if (!range) return;
    setFromDate(toISODate(range.from()));
    setToDate(toISODate(range.to()));
    setActiveRange(key);
  }, []);

  const handleFromChange = (v) => { setFromDate(v); setActiveRange(""); };
  const handleToChange = (v) => { setToDate(v); setActiveRange(""); };

  const clearFilters = () => {
    const d = getDefaults();
    setFromDate(d.fromDate);
    setToDate(d.toDate);
    setActiveRange(d.activeRange);
    setRepFilter(d.repFilter);
    setRegionFilter(d.regionFilter);
    setSearch(d.search);
  };

  const isManager = user?.role === "manager" || user?.role === "admin";

  // Inline editing state
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [conflictWarning, setConflictWarning] = useState(null);
  const [conflictChecking, setConflictChecking] = useState(false);

  const checkAppointmentConflicts = async (date, time, checkForRepName = null) => {
    if (!date || !time) return;
    let repName = checkForRepName;
    if (!repName) repName = user?.assigned_rep_name || user?.name;
    if (!repName) return;
    setConflictChecking(true);
    setConflictWarning(null);
    try {
      const response = await axios.post(`${API}/calendar/appointments/check-conflicts`, {
        rep_name: repName, appointment_date: date, appointment_time: time, duration_minutes: 60
      });
      setConflictWarning(response.data.has_conflicts ? response.data.conflicts : null);
    } catch { setConflictWarning(null); } finally { setConflictChecking(false); }
  };

  const canEdit = (appt) => {
    if (isManager) return true;
    return appt.rep_name === user?.name || appt.rep_name === user?.assigned_rep_name;
  };

  const startEdit = (appt) => {
    setEditingId(appt.appointment_id);
    setEditData({
      appointment_date: appt.appointment_date || "",
      appointment_time: appt.appointment_time || "",
      visit_type: appt.visit_type || "",
      notes: appt.notes || "",
      rep_name: appt.rep_name || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
    setConflictWarning(null);
    setConflictChecking(false);
  };

  const saveEdit = async (apptId) => {
    setSaving(true);
    try {
      const payload = {
        appointment_date: editData.appointment_date,
        appointment_time: editData.appointment_time || null,
        visit_type: editData.visit_type,
        notes: editData.notes,
      };
      if (isManager) {
        payload.assigned_rep = editData.rep_name;
      }
      await axios.patch(`${API}/calendar/appointments/${apptId}`, payload, { withCredentials: true });

      // Optimistic update
      setAppointments(prev => prev.map(a => {
        if (a.appointment_id !== apptId) return a;
        return {
          ...a,
          appointment_date: editData.appointment_date,
          appointment_time: editData.appointment_time || null,
          visit_type: editData.visit_type,
          notes: editData.notes,
          rep_name: isManager ? editData.rep_name : a.rep_name,
        };
      }).filter(a => {
        // Remove if date moved outside filter range
        if (a.appointment_id === apptId) {
          return a.appointment_date >= fromDate && a.appointment_date <= toDate;
        }
        return true;
      }));

      toast.success("Appointment updated");
      setEditingId(null);
      setEditData({});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update appointment");
    } finally {
      setSaving(false);
    }
  };

  const hasActiveFilters = repFilter || regionFilter || search ||
    fromDate !== defaults.fromDate || toDate !== defaults.toDate;

  const filtered = useMemo(() => {
    let result = appointments;

    // Client-side from-date filter
    if (fromDate) {
      result = result.filter(a => a.appointment_date >= fromDate);
    }

    // Client-side region filter
    if (regionFilter) {
      result = result.filter(a => a.area === regionFilter);
    }

    // Client-side search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        (a.doctor_name || "").toLowerCase().includes(q) ||
        (a.practice_name || "").toLowerCase().includes(q)
      );
    }

    return result;
  }, [appointments, fromDate, regionFilter, search]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      const date = a.appointment_date;
      if (!map[date]) map[date] = [];
      map[date].push(a);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const rangeLabel = activeRange
    ? QUICK_RANGES.find(r => r.key === activeRange)?.label || ""
    : `${fromDate} to ${toDate}`;

  return (
    <div className="p-4 lg:p-6 space-y-4 pb-6" data-testid="upcoming-page">
      <div>
        <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope, sans-serif" }}>
          Upcoming
        </h1>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1">All planned appointments</p>
      </div>

      {/* Filters */}
      <div className="bg-muted/50 rounded-lg p-3 space-y-3">
        {/* Row 1: Quick ranges + date inputs */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
          <div className="flex gap-1 p-1 bg-muted rounded-lg shrink-0">
            {QUICK_RANGES.map(r => (
              <button
                key={r.key}
                onClick={() => applyQuickRange(r.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  activeRange === r.key
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-end">
            <div>
              <Label className="text-[11px] text-muted-foreground">From</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => handleFromChange(e.target.value)}
                className="h-9 w-36 text-xs"
                data-testid="filter-from-date"
              />
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">To</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => handleToChange(e.target.value)}
                className="h-9 w-36 text-xs"
                data-testid="filter-to-date"
              />
            </div>
          </div>
        </div>

        {/* Row 2: Rep + Region + Search + Clear */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
          {isManager && (
            <div className="w-44">
              <Label className="text-[11px] text-muted-foreground">Rep</Label>
              <Select value={repFilter || "ALL"} onValueChange={(v) => setRepFilter(v === "ALL" ? "" : v)}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="All reps" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All reps</SelectItem>
                  {REP_DIRECTORY.map(r => (
                    <SelectItem key={r} value={r}>{getRepDisplayName(r)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="w-36">
            <Label className="text-[11px] text-muted-foreground">Region</Label>
            <Select value={regionFilter || "ALL"} onValueChange={(v) => setRegionFilter(v === "ALL" ? "" : v)}>
              <SelectTrigger className="h-9 text-xs" data-testid="filter-region">
                <SelectValue placeholder="All regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All regions</SelectItem>
                {AREAS.map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="relative flex-1 min-w-[180px]">
            <Label className="text-[11px] text-muted-foreground">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Doctor or practice..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9 text-xs"
                data-testid="search-upcoming"
              />
            </div>
          </div>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-xs shrink-0" data-testid="clear-filters">
              <X className="w-3.5 h-3.5 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : grouped.length === 0 ? (
        <div className="text-center py-16">
          <CalendarCheck className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground font-medium">No upcoming appointments for {rangeLabel}</p>
          <p className="text-sm text-muted-foreground mt-1">Planned appointments will appear here</p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([date, items]) => (
            <div key={date}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-2 h-2 rounded-full ${isToday(date) ? "bg-orange-500" : "bg-blue-500"}`} />
                <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: "Manrope, sans-serif" }}>
                  {formatDate(date)}
                </h2>
                {isToday(date) && <Badge className="bg-orange-100 text-orange-700 text-[10px]">Today</Badge>}
                <Badge variant="secondary" className="text-[10px]">{items.length}</Badge>
              </div>
              <div className="grid gap-2">
                {items.map((appt, i) => (
                  <Card key={appt.appointment_id || i} className="group border hover:border-primary/30 transition-colors" data-testid={`upcoming-card-${appt.appointment_id || i}`}>
                    <CardContent className="p-4">
                      {editingId === appt.appointment_id ? (
                        /* Edit mode */
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Stethoscope className="w-4 h-4 text-primary shrink-0" />
                            <span className="font-semibold text-sm">{appt.doctor_name}</span>
                            {appt.practice_name && <span className="text-xs text-muted-foreground">{appt.practice_name}</span>}
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <div>
                              <Label className="text-[11px] text-muted-foreground">Date</Label>
                              <Input
                                type="date"
                                value={editData.appointment_date}
                                onChange={(e) => {
                                  const nd = e.target.value;
                                  setEditData({ ...editData, appointment_date: nd });
                                  if (nd && editData.appointment_time) checkAppointmentConflicts(nd, editData.appointment_time, editData.rep_name || null);
                                  else { setConflictWarning(null); }
                                }}
                                className="h-9 text-xs"
                              />
                            </div>
                            <div>
                              <Label className="text-[11px] text-muted-foreground">Time</Label>
                              <Input
                                type="time"
                                value={editData.appointment_time}
                                onChange={(e) => {
                                  const nt = e.target.value;
                                  setEditData({ ...editData, appointment_time: nt });
                                  if (editData.appointment_date && nt) checkAppointmentConflicts(editData.appointment_date, nt, editData.rep_name || null);
                                  else { setConflictWarning(null); }
                                }}
                                className="h-9 text-xs"
                              />
                              {conflictChecking && (
                                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                  <span className="animate-spin inline-block w-3 h-3 border border-current border-t-transparent rounded-full" />
                                  Checking calendar...
                                </p>
                              )}
                              {conflictWarning && conflictWarning.length > 0 && (
                                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                                  <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                      <p className="text-[11px] font-semibold text-amber-800">
                                        Conflict — {conflictWarning.length} appointment{conflictWarning.length > 1 ? "s" : ""} within 1 hour
                                      </p>
                                      {conflictWarning.map((c, ci) => (
                                        <div key={ci} className="text-[10px] text-amber-700 mt-1">
                                          {c.doctor_name} · {c.appointment_time} · {c.visit_type}
                                        </div>
                                      ))}
                                      <p className="text-[10px] text-amber-600 mt-1">Warning only — you can still save.</p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div>
                              <Label className="text-[11px] text-muted-foreground">Type</Label>
                              <Select value={editData.visit_type || "NONE"} onValueChange={(v) => setEditData({ ...editData, visit_type: v === "NONE" ? "" : v })}>
                                <SelectTrigger className="h-9 text-xs">
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="NONE">No type</SelectItem>
                                  {APPOINTMENT_TYPES.map(t => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                  ))}
                                  <SelectItem value="Follow-up">Follow-up</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          {isManager && (
                            <div className="max-w-xs">
                              <Label className="text-[11px] text-muted-foreground">Rep</Label>
                              <Select value={editData.rep_name || "NONE"} onValueChange={(v) => setEditData({ ...editData, rep_name: v === "NONE" ? "" : v })}>
                                <SelectTrigger className="h-9 text-xs">
                                  <SelectValue placeholder="Select rep" />
                                </SelectTrigger>
                                <SelectContent>
                                  {REP_DIRECTORY.map(r => (
                                    <SelectItem key={r} value={r}>{getRepDisplayName(r)}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          <div>
                            <Label className="text-[11px] text-muted-foreground">Notes</Label>
                            <Textarea
                              value={editData.notes}
                              onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                              placeholder="Notes..."
                              rows={2}
                              className="text-xs"
                            />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={saving} className="h-8 text-xs">
                              Cancel
                            </Button>
                            <Button size="sm" onClick={() => saveEdit(appt.appointment_id)} disabled={saving} className="h-8 text-xs gap-1">
                              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                              Save
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* Read-only mode */
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0 space-y-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Stethoscope className="w-4 h-4 text-primary shrink-0" />
                              <span className="font-semibold text-sm truncate">{appt.doctor_name}</span>
                              {appt.practice_name && (
                                <span className="text-xs text-muted-foreground truncate">{appt.practice_name}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="text-[11px]">
                                <User className="w-3 h-3 mr-1" />
                                {getRepDisplayName(appt.rep_name)}
                              </Badge>
                              {appt.visit_type && (
                                <Badge variant="secondary" className="text-[11px]">{appt.visit_type}</Badge>
                              )}
                              {appt.area && (
                                <Badge variant="secondary" className="text-[11px]">
                                  <MapPin className="w-3 h-3 mr-1" />
                                  {appt.area}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-start gap-2 shrink-0">
                            <div className="text-right">
                              <div className="flex items-center gap-1 text-sm font-medium">
                                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                                {appt.appointment_time || "Unscheduled"}
                              </div>
                            </div>
                            {canEdit(appt) && (
                              <button
                                onClick={() => startEdit(appt)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
                                data-testid={`edit-btn-${appt.appointment_id}`}
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UpcomingPage;
